import React, { useState, useEffect } from "react";
import { X } from "lucide-react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { subcategoryService } from "../../services/subcategoryService";

const ProductForm = ({
  isOpen,
  onClose,
  onSubmit,
  initialData = null,
  categories = [],
}) => {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    price: "",
    discount: 0,
    priceAfterDiscount: 0,
    quantity: 0,
    category: "",
    subCategory: "",
    imageCover: null,
    soldOut: false,
    images: [],
    IsTrending: false,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [subcategories, setSubcategories] = useState([]);

  useEffect(() => {
    const fetchSubcategories = async () => {
      if (formData.category) {
        try {
          const response = await subcategoryService.getSubcategoriesByCategory(
            formData.category
          );
          setSubcategories(response.subCategories);
        } catch (err) {
          console.error("Error fetching subcategories:", err);
          toast.error("Failed to load subcategories");
        }
      } else {
        setSubcategories([]);
      }
    };

    fetchSubcategories();
  }, [formData.category]);
  
  useEffect(() => {
    if (initialData) {
      setFormData({
        title: initialData.title || "",
        description: initialData.description || "",
        price: initialData.price?.toString() || "",
        discount: initialData.discount || 0,
        priceAfterDiscount: initialData.priceAfterDiscount || 0,
        quantity: initialData.quantity || 0,
        category: initialData.category?._id || initialData.category || "",
        subCategory: initialData.subCategory || "",
        imageCover: initialData.imageCover || null,
        images: initialData.images || [], // Populate existing images directly
        IsTrending: initialData.IsTrending || false,
        soldOut: initialData.soldOut || false,
      });
    } else {
      resetForm();
    }
  }, [initialData, isOpen]);

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      price: "",
      discount: 0,
      priceAfterDiscount: 0,
      quantity: 0,
      category: "",
      subCategory: "",
      imageCover: null,
      images: [],
      IsTrending: false,
      soldOut: false,
    });
    setError(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    if (name === "images") {
      // Add new images to existing images
      const newImages = Array.from(files);
      setFormData((prev) => ({
        ...prev,
        images: [...prev.images, ...newImages],
      }));
    } else {
      // For single file inputs like imageCover
      setFormData((prev) => ({
        ...prev,
        [name]: files[0],
      }));
    }
  };

  const handleDeleteExistingImage = (index) => {
    setFormData((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const submitData = new FormData();

    // Append all form fields
    Object.entries(formData).forEach(([key, value]) => {
      // Special handling for category to ensure it's the ID
      if (key === "category") {
        submitData.append(key, value);
      } else if (key !== "images" && key !== "imageCover") {
        if (value !== "" && value !== null) {
          submitData.append(key, value);
        }
      }
    });

    // Handle image cover
    if (formData.imageCover instanceof File) {
      submitData.append("imageCover", formData.imageCover);
    }

    // Append images
    formData.images.forEach((file) => {
      submitData.append("images", file);
    });

    try {
      await onSubmit(submitData);
      toast.success(
        initialData
          ? "Product updated successfully!"
          : "Product added successfully!"
      );
      onClose();
    } catch (error) {
      console.error("Submission error:", error);
      toast.error(
        error.response?.data?.message ||
          "An error occurred while saving the product."
      );
      setError(error.response?.data?.message || "Submission failed");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[95vh] overflow-y-auto">
        <div className="flex justify-between items-center p-4 border-b">
          <h2 className="text-xl font-bold">
            {initialData ? "Edit Product" : "Add New Product"}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-600 hover:text-gray-900"
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {error && (
            <div
              className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative"
              role="alert"
            >
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Price
              </label>
              <input
                type="number"
                name="price"
                value={formData.price}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="0"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Select a category</option>
                {categories.map((cat) => (
                  <option key={cat._id} value={cat._id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Subcategory Select - Only show if category is selected and subcategories exist */}
          {formData.category && subcategories?.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Subcategory
              </label>
              <select
                name="subCategory"
                value={formData.subCategory}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Select a subcategory</option>
                {subcategories.map((subCat) => (
                  <option key={subCat._id} value={subCat._id}>
                    {subCat.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 h-20"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Image Cover
              </label>
              <input
                type="file"
                name="imageCover"
                onChange={handleFileChange}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                accept="image/*"
                required={!initialData}
              />
              {formData.imageCover && formData.imageCover instanceof File && (
                <div className="mt-2">
                  <img
                    src={URL.createObjectURL(formData.imageCover)}
                    alt="Cover"
                    className="w-full h-32 object-cover"
                  />
                </div>
              )}
              {initialData?.imageCover && !formData.imageCover && (
                <div className="flex items-center mt-2">
                  <img
                    src={initialData.imageCover}
                    alt="Current Cover"
                    className="w-full h-32 object-cover"
                  />
                </div>
              )}
            </div>

            <div>
              <div>
                <label className="block  text-sm font-medium text-gray-700 mb-1">
                  Images
                </label>
                <input
                  type="file"
                  name="images"
                  onChange={handleFileChange}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  accept="image/*"
                  multiple
                />

                {/* Existing Images */}
                {formData.images.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {formData.images.map((image, index) => (
                      <div key={index} className="relative">
                        <img
                          src={
                            typeof image === "string"
                              ? image
                              : URL.createObjectURL(image)
                          }
                          alt={`Image ${index + 1}`}
                          className="w-16 h-16 object-cover rounded"
                        />
                        <button
                          type="button"
                          onClick={() => handleDeleteExistingImage(index)}
                          className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-1"
                        >
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div>
            <label className="inline-flex items-center space-x-2">
              <input
                type="checkbox"
                name="IsTrending"
                checked={formData.IsTrending}
                onChange={() =>
                  setFormData((prev) => ({
                    ...prev,
                    IsTrending: !prev.IsTrending,
                  }))
                }
                className="form-checkbox"
              />
              <span>Is Trending</span>
            </label>
          </div>
          <div>
            <label className="inline-flex items-center space-x-2">
              <input
                type="checkbox"
                name="soldOut"
                checked={formData.soldOut}
                onChange={() =>
                  setFormData((prev) => ({
                    ...prev,
                    soldOut: !prev.soldOut,
                  }))
                }
                className="form-checkbox"
              />
              <span>sold Out</span>
            </label>
          </div>

          <div className="flex justify-between items-center">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 bg-gray-500 text-white rounded-lg"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className={`px-6 py-2 rounded-lg text-white ${
                loading
                  ? "bg-gray-500 cursor-not-allowed"
                  : "bg-blue-500 hover:bg-blue-700"
              }`}
            >
              {loading
                ? "Saving..."
                : initialData
                ? "Update Product"
                : "Add Product"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProductForm;
